import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

import {
  ActionRecord,
  CreateActionRequest,
  UpdateActionRequest,
} from "../ActionRecord";

interface ActionOptions {
  throwOnError: boolean;
  resourceOwnerId: string;
}

/**
 * The cache key of the action `name` that the org `ownerOrgId` owns, at the
 * version `digest` names, or at its latest when `digest` is undefined. Two
 * orgs' actions of one name, and two versions of one action, are different
 * records. Every key starts with `["actionRecord", name]`, so removing queries
 * by that prefix drops every org's and every version's record of the name.
 */
export function actionRecordQueryKey(
  name: string | undefined,
  digest: string | undefined,
  ownerOrgId: string | undefined,
) {
  return [
    "actionRecord",
    name,
    { digest: digest ?? null, ownerOrgId: ownerOrgId ?? null },
  ] as const;
}

export function useAction(
  name: string | undefined,
  digest: string | undefined,
  options?: Partial<ActionOptions>,
) {
  const { actionService } = useDomainServices();

  return useQuery<ActionRecord, Error>({
    queryKey: actionRecordQueryKey(name, digest, options?.resourceOwnerId),
    queryFn({ signal }) {
      if (name === undefined) {
        return Promise.reject(new Error("Invalid action name"));
      }

      return actionService.getAction(name, digest, {
        abortSignal: signal,
        resourceOwnerId: options?.resourceOwnerId,
      });
    },
    enabled: name !== undefined,
    // 1 Minute until stale
    staleTime: 60000,
    throwOnError: options?.throwOnError ?? false,
  });
}

export function useCreateAction() {
  const { actionService } = useDomainServices();
  const { currentOrganization } = useAuth();

  const queryClient = useQueryClient();

  return useMutation<ActionRecord, Error, CreateActionRequest>({
    mutationFn: (request) => {
      if (currentOrganization === null) {
        return Promise.reject(new Error("Requires login"));
      }

      return actionService.createAction(request, currentOrganization.org_id);
    },

    onSuccess: (actionRecord) => {
      queryClient.setQueryData(
        actionRecordQueryKey(actionRecord.name, undefined, actionRecord.org_id),
        actionRecord,
      );

      return queryClient.invalidateQueries({ queryKey: ["actions"] });
    },
  });
}

export interface UpdateAction {
  name: string;
  resourceOwnerId: string;
  updates: UpdateActionRequest;
}

export function useUpdateAction() {
  const { actionService } = useDomainServices();

  const queryClient = useQueryClient();

  return useMutation<ActionRecord, Error, UpdateAction>({
    mutationFn: ({ name, resourceOwnerId, updates }) => {
      return actionService.updateAction(name, updates, {
        resourceOwnerId,
      });
    },

    onSuccess: (actionRecord) => {
      queryClient.setQueryData(
        actionRecordQueryKey(actionRecord.name, undefined, actionRecord.org_id),
        actionRecord,
      );

      return queryClient.invalidateQueries({ queryKey: ["actions"] });
    },
  });
}

interface DeleteActionParams {
  name: ActionRecord["name"];
  resourceOwnerId: string;
}

export function useDeleteAction() {
  const { actionService } = useDomainServices();

  const queryClient = useQueryClient();

  return useMutation<void, Error, DeleteActionParams>({
    mutationFn: ({ name, resourceOwnerId }) => {
      return actionService.deleteAction(name, { resourceOwnerId });
    },

    onSuccess: (_data, { name }) => {
      queryClient.removeQueries({ queryKey: ["actionRecord", name] });

      return queryClient.invalidateQueries({ queryKey: ["actions"] });
    },
  });
}
